import React, {Component} from 'react';
import {addAssigneeToOrg, getOrgAssignees, removeAssigneeFromOrg} from '../../services/orgService';
import ErrorAlert from "../../components/ErrorAlert";
import SectionCard from "../../patterns/SectionCard";
import {SimpleList, SimpleListRow} from "../../patterns/SimpleList";
import EmptyState from "../../patterns/EmptyState";
import {PanelEmptyIllustration} from "../../patterns/EmptyStateIllustrations";
import FormDialog from "../../patterns/FormDialog";
import ConfirmDialog from "../../patterns/datalist/ConfirmDialog";
import Toast from "../../patterns/Toast";
import TextInput from "../../patterns/form/TextInput";
import Banner from "../../patterns/Banner";
import styles from './OrgPanel.module.css';

export default class Assignees extends Component {
    constructor(props) {
        super(props);
        this.state = {
            assignees: null,
            nameToAdd: '',

            showCreateModal: false,
            addBusy: false,

            assigneeToDelete: null,
            assigneeName: null,
            removeBusy: false,

            addAssigneeToOrgError: null,
            removeAssigneeFromOrgError: null,
            persistentErrorMessage: null,
            toast: null,
        }
    }

    async componentDidMount() {
        try {
            const assigneeResponse = await getOrgAssignees(this.props.org.id);
            this.setState({assignees: assigneeResponse.assignees});
        } catch (error) {
            this.setState({
                persistentErrorMessage: `Problem getting assignees: ${error.toString()}`
            });
        }
    }

    cancelAddAssignee = () => {
        this.setState({showCreateModal: false, nameToAdd: '', addAssigneeToOrgError: null});
    }

    addClicked = () => {
        this.setState({showCreateModal: true});
    }

    nameChanged = (event) => {
        this.setState({nameToAdd: event.target.value});
    }

    addAssigneeToOrg = async () => {
        this.setState({addBusy: true});
        try {
            await addAssigneeToOrg(this.props.org.id, this.state.nameToAdd);
            const name = this.state.nameToAdd;
            await this.refreshAssignees();
            this.setState({
                showCreateModal: false,
                nameToAdd: '',
                addBusy: false,
                toast: {message: `${name} added to org`}
            })
        } catch (e) {
            let message = `Problem adding assignee to org: ${e.toString()}`;
            if (e.response?.data?.error === 'Invalid value') message = `Invalid assignee value`;
            else if (e.response?.data?.error) message = e.response.data.error;
            this.setState({addAssigneeToOrgError: message, addBusy: false});
        }
    }

    cancelDelete = () => {
        this.setState({assigneeToDelete: null, removeAssigneeFromOrgError: null});
    }

    deleteClicked = (id, name) => {
        this.setState({assigneeToDelete: id, assigneeName: name})
    };

    removeAssigneeFromOrg = async () => {
        const name = this.state.assigneeName;
        this.setState({removeBusy: true});
        try {
            await removeAssigneeFromOrg(this.props.org.id, this.state.assigneeToDelete);
            await this.refreshAssignees();
            this.setState({
                assigneeToDelete: null,
                removeBusy: false,
                toast: {message: `${name} removed from org`}
            })
        } catch (e) {
            let message = `Problem removing assignee from org: ${e.toString()}`;
            if (e.response?.data?.error === 'Invalid value') message = `Invalid assignee value`;
            else if (e.response?.data?.error) message = e.response.data.error;
            this.setState({removeAssigneeFromOrgError: message, removeBusy: false});
        }
    }

    refreshAssignees = async () => {
        const assigneesResponse = await getOrgAssignees(this.props.org.id);
        this.setState({assignees: assigneesResponse.assignees});
    }

    dismissToast = () => this.setState({toast: null});

    render() {
        const {assignees, showCreateModal, nameToAdd, addBusy, addAssigneeToOrgError,
            assigneeToDelete, assigneeName, removeBusy, removeAssigneeFromOrgError,
            persistentErrorMessage, toast} = this.state;

        return (
            <div>
                <SectionCard
                    title="Assignees"
                    action={
                        <button type="button" className={styles.addButton} disabled={this.props.disabled}
                                onClick={this.addClicked}>
                            + Add assignee
                        </button>
                    }
                >
                    {!assignees && !persistentErrorMessage && <p className={styles.loading}>Loading assignees…</p>}
                    <ErrorAlert message={persistentErrorMessage} delay={0}/>
                    {!this.props.isOwner && assignees && (
                        <Banner tone="info">You cannot modify assignees. You are not the org owner.</Banner>
                    )}
                    {assignees && (assignees.length ? (
                        <SimpleList>
                            {assignees.map((assignee) => (
                                <SimpleListRow
                                    key={assignee.id}
                                    primary={assignee.name}
                                    removeLabel={`Remove ${assignee.name} from org`}
                                    onRemove={this.props.disabled ? undefined : () => this.deleteClicked(assignee.id, assignee.name)}
                                />
                            ))}
                        </SimpleList>
                    ) : (
                        <EmptyState
                            size="panel"
                            illustration={<PanelEmptyIllustration/>}
                            title="No assignees yet"
                            description="Add assignees so tasks can be handed out to them."
                        />
                    ))}
                </SectionCard>

                <FormDialog
                    open={showCreateModal}
                    title={`Add an assignee to ${this.props.org.name}`}
                    onCancel={this.cancelAddAssignee}
                    onSubmit={this.addAssigneeToOrg}
                    submitLabel="Add"
                    submitDisabled={!nameToAdd}
                    busy={addBusy}
                >
                    {(firstFieldRef) => (
                        <TextInput
                            ref={firstFieldRef}
                            id="add-assignee-name"
                            label="Name"
                            value={nameToAdd}
                            onChange={this.nameChanged}
                            error={addAssigneeToOrgError}
                        />
                    )}
                </FormDialog>

                <ConfirmDialog
                    open={!!assigneeToDelete}
                    title="Remove assignee?"
                    body={`Are you sure you want to remove ${assigneeName} from this org?`}
                    confirmLabel="Remove"
                    onConfirm={this.removeAssigneeFromOrg}
                    onCancel={this.cancelDelete}
                    busy={removeBusy}
                    error={removeAssigneeFromOrgError}
                />

                {toast && <Toast message={toast.message} onDismiss={this.dismissToast}/>}
            </div>
        );
    }
}
